/**
 * A carried question's options (`response.choicesFrom`): the one owner (C07-1).
 *
 * The source question owns the answers a carry-forward shows. The target
 * stores a copy only so the readers of `response.options` (analysis, the
 * editor, the report) see the list: that copy is derived here from the source,
 * never written by a model or an author. The applier derives it after every
 * batch, the artifact builder at normalize, and the editor when a link is set,
 * so "a carried target's values equal its source's" holds by construction:
 * the validator's `choices_from_value_set_mismatch` is an invariant.
 *
 * An option keeps the id it had on the target under the same value, so the
 * answers already joined to it stay joined; a new value gets
 * `<target>_<value>`, unique in the list. A ranked target takes no
 * `exclusive` or `keepLast` flag (D0a: an escape inside a ranked list means
 * nothing, and a ranking is never shuffled), as every ranking write strips
 * them; a source with an escape answer therefore cannot feed a ranking
 * (`choices_from_option_metadata_mismatch`).
 */

/** The target kinds whose options take the escape and keep-last flags (D0/D0a). */
const FLAGGED_KINDS = new Set(["single_select", "multi_select"]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** The fields a carried option takes from its source option. */
export interface CarriedChoiceOption {
  id: string;
  value: string;
  label: string;
  description?: string;
  exclusive?: true;
  keepLast?: true;
}

/**
 * The target's options as its source's: each source option with the target's
 * id for that value. `previous` is the target's current list.
 */
export function carriedChoiceOptions(
  targetStableKey: string,
  targetKind: unknown,
  sourceOptions: ReadonlyArray<unknown>,
  previous: ReadonlyArray<unknown>,
): Array<CarriedChoiceOption> {
  const flagged = typeof targetKind === "string" && FLAGGED_KINDS.has(targetKind);
  const kept = previous.filter(isRecord);
  const ids = new Set<string>(kept.map((option) => String(option.id ?? "")));
  return sourceOptions.filter(isRecord).map((option) => {
    const value = String(option.value ?? "");
    let id = kept.find((candidate) => candidate.value === value)?.id;
    if (typeof id !== "string" || !id) {
      id = `${targetStableKey}_${value}`;
      for (let suffix = 2; ids.has(id as string); suffix += 1) {
        id = `${targetStableKey}_${value}_${suffix}`;
      }
      ids.add(id as string);
    }
    return {
      id: id as string,
      value,
      label: String(option.label ?? ""),
      ...(typeof option.description === "string" && option.description
        ? { description: option.description }
        : {}),
      ...(flagged && option.exclusive === true ? { exclusive: true as const } : {}),
      ...(flagged && option.keepLast === true ? { keepLast: true as const } : {}),
    };
  });
}

/**
 * Every carried question with its options derived from its source, by stable
 * key. A question whose source is missing or has no option list is returned
 * as is: the validator refuses that link (`choices_from_invalid_reference`,
 * `choices_from_unsupported_source_type`). Returns `questions` itself when
 * nothing changes.
 */
export function withCarriedChoices<T>(questions: ReadonlyArray<T>): Array<T> {
  const byKey = new Map<unknown, unknown>(
    (questions as ReadonlyArray<unknown>)
      .filter(isRecord)
      .map((question) => [question.stableKey, question]),
  );
  let changed = false;
  const next = questions.map((entry) => {
    const question: unknown = entry;
    if (!isRecord(question) || !isRecord(question.response)) return entry;
    const response = question.response;
    const carried = response.choicesFrom;
    if (!isRecord(carried)) return entry;
    const source = byKey.get(carried.sourceStableKey);
    const sourceOptions =
      isRecord(source) && isRecord(source.response)
        ? source.response.options
        : undefined;
    if (!Array.isArray(sourceOptions) || source === question) return entry;
    const previous = Array.isArray(response.options) ? response.options : [];
    const options = carriedChoiceOptions(
      String(question.stableKey ?? ""),
      response.kind ?? question.type,
      sourceOptions,
      previous,
    );
    if (JSON.stringify(options) === JSON.stringify(previous)) return entry;
    changed = true;
    return { ...question, response: { ...response, options } } as unknown as T;
  });
  return changed ? next : (questions as Array<T>);
}
